// @ts-nocheck

import { timeline, easing } from 'popmotion';
import React, { useEffect, useCallback, useRef } from 'react';
import styled, { css } from 'styled-components';

import { ReactComponent as ArrowDown } from '../../../assets/svg/chevron-down.svg';
import { ReactComponent as ArrowUp } from '../../../assets/svg/chevron-up.svg';
import { usePrevious } from '../../../hooks';
import Formatter from '../../../i18n/formatter';
import { OutcomeModes } from '../../../types/outcome';

import ScrollingLabel from './ScrollingLabel';

const commonIcon = css`
  width: 1em;
  height: 1em;
  position: absolute;
  left: calc(50% - 0.5em);
  opacity: 0;
`;

const ArrowUpIcon = styled(ArrowUp)`
  ${commonIcon}
  color: ${({ theme }) => theme.common.colors.positive};
  top: calc(40% - 0.5em);
`;

const ArrowDownIcon = styled(ArrowDown)`
  ${commonIcon}
  color: ${({ theme }) => theme.common.colors.negative};
  bottom: calc(40% - 0.5em);
`;

const Wrapper = styled.button`
  padding: 0 0.5em;
  height: 3em;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  && {
    margin: 0.2em 0;
    background: rgba(255, 255, 255, 0.8);
    border-radius: 2px;
    overflow: hidden;
  }
`;

const Odd = styled.span`
  position: relative;
  text-align: right;
  && {
    color: ${({ $mode, theme }) =>
      $mode === OutcomeModes.PLAY ? theme.common.colors.primary : theme.common.colors.mild};
  }
`;

const LabelOutcome = styled(ScrollingLabel)`
  width: 75%;
  text-align: left;
  color: #000;
`;

const TweenUp = [
  { track: 'arrow', from: [0, 0], to: [-40, 1], duration: 500, ease: easing.easeInOut },
  { track: 'arrow', from: [-40, 1], to: [-80, 0], duration: 500, ease: easing.easeInOut },
];
const TweenDown = [
  { track: 'arrow', from: [0, 0], to: [40, 1], duration: 500, ease: easing.easeInOut },
  { track: 'arrow', from: [40, 1], to: [80, 0], duration: 500, ease: easing.easeInOut },
];

const ImmersiveOutcomeWithVariation = (props) => {
  const { label, handleClick, mode, oddValue } = props;

  const previousOddValue = usePrevious(oddValue);
  const arrowUpNode = useRef(null);
  const arrowDownNode = useRef(null);
  const sub = useRef(null);

  const onTweenUpUpdate = useCallback(
    (v) => {
      if (arrowUpNode.current !== null && arrowUpNode.current.style) {
        arrowUpNode.current.style.transform = `translateY(${v.arrow[0]}%)`;
        arrowUpNode.current.style.opacity = v.arrow[1];
      }
    },
    [arrowUpNode],
  );
  const onTweenDownUpdate = useCallback(
    (v) => {
      if (arrowDownNode.current !== null && arrowDownNode.current.style) {
        arrowDownNode.current.style.transform = `translateY(${v.arrow[0]}%)`;
        arrowDownNode.current.style.opacity = v.arrow[1];
      }
    },
    [arrowDownNode],
  );

  const stopAnimation = useCallback(() => {
    if (sub.current) {
      sub.current.seek(1);
      sub.current.stop();
      sub.current = null;
      if (arrowDownNode.current) {
        arrowDownNode.current.style.opacity = 0;
      }
      if (arrowUpNode.current) {
        arrowUpNode.current.style.opacity = 0;
      }
    }
  }, [arrowDownNode, arrowUpNode]);

  useEffect(() => () => stopAnimation());

  useEffect(() => {
    if (mode === OutcomeModes.PLAY) {
      if (oddValue > previousOddValue) {
        stopAnimation();
        sub.current = timeline(TweenUp, { loop: 4 }).start(onTweenUpUpdate);
      } else if (oddValue < previousOddValue) {
        stopAnimation();
        sub.current = timeline(TweenDown, { loop: 4 }).start(onTweenDownUpdate);
      }
    } else {
      stopAnimation();
    }
  }, [oddValue, previousOddValue, mode, onTweenUpUpdate, onTweenDownUpdate, arrowDownNode, arrowUpNode, stopAnimation]);

  const onClick = useCallback(() => {
    if (mode === OutcomeModes.PLAY) {
      handleClick(oddValue);
    }
  }, [handleClick, mode, oddValue]);

  return (
    <Wrapper onClick={onClick}>
      <LabelOutcome label={label} />
      <Odd $mode={mode}>
        <ArrowDownIcon ref={arrowDownNode} />
        <ArrowUpIcon ref={arrowUpNode} />
        {mode === OutcomeModes.STOP ? <span>--</span> : null}
        {mode === OutcomeModes.PAUSE ? <strike>{Formatter.formatOdd(oddValue)}</strike> : null}
        {mode === OutcomeModes.PLAY || mode === OutcomeModes.STOP_BUT_DISPLAYED ? (
          <span>{Formatter.formatOdd(oddValue)}</span>
        ) : null}
      </Odd>
    </Wrapper>
  );
};

export default ImmersiveOutcomeWithVariation;
